import React, { useState } from "react";
import { Calendar, User, Package, Edit, AlertTriangle, AlertCircle, Clock, ChevronRight } from "lucide-react";
import { ComponentProps } from "../types/types";
import { supabaseService } from "../SupabaseService";

const TilBestilling: React.FC<ComponentProps> = ({
  cases,
  setCases,
  customers,
  setCustomers,
  openCaseForEditing,
}) => {
  const [isMoving, setIsMoving] = useState<number | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);

  const tilBestillingCases = cases.filter(
    (case_) => case_.kategori === "Til bestilling"
  );

  // Grupper ordre etter kunde
  const groupedCases = tilBestillingCases.reduce((groups, case_) => {
    const customerName = case_.kunde;
    if (!groups[customerName]) {
      groups[customerName] = [];
    }
    groups[customerName].push(case_);
    return groups;
  }, {} as Record<string, typeof tilBestillingCases>);

  const handleMoveToCategory = async (caseId: number, newCategory: string) => {
    setIsMoving(caseId);
    setMoveError(null);
    const originalCases = [...cases];

    try {
      const caseToUpdate = cases.find(c => c.id === caseId);
      if (!caseToUpdate) throw new Error("Sak ikke funnet");

      const today = new Date().toISOString().split("T")[0];
      const updatedCase = {
        ...caseToUpdate,
        kategori: newCategory,
        delebestillingDato: newCategory === "Bestilt" ? today : caseToUpdate.delebestillingDato
      };

      setCases(prev =>
        prev.map(case_ =>
          case_.id === caseId ? updatedCase : case_
        )
      );

      const saved = await supabaseService.saveCase(updatedCase);
      if (!saved) {
        throw new Error("Kunne ikke lagre endring");
      }

      console.log(`✅ Flyttet sak ${caseId} til ${newCategory}`);
    } catch (error) {
      setCases(originalCases);
      setMoveError(`Feil ved flytting: ${error.message}`);
      console.error("Feil ved flytting av sak:", error);
    } finally {
      setIsMoving(null);
    }
  };

  const getUrgencyColor = (caseDate: string) => {
    const now = new Date();
    const created = new Date(caseDate);
    const hoursDiff = (now.getTime() - created.getTime()) / (1000 * 60 * 60);

    if (hoursDiff > 72) return "border-2 border-red-200 bg-red-50 shadow-sm hover:shadow-lg hover:border-red-300";
    if (hoursDiff > 48) return "border-2 border-orange-200 bg-orange-50 shadow-sm hover:shadow-lg hover:border-orange-300";
    return "border-2 border-yellow-200 bg-yellow-50 shadow-sm hover:shadow-lg hover:border-yellow-300";
  };

  const getUrgencyBadge = (caseDate: string) => {
    const now = new Date();
    const created = new Date(caseDate);
    const hoursDiff = (now.getTime() - created.getTime()) / (1000 * 60 * 60);
    const days = Math.floor(hoursDiff / 24);

    if (hoursDiff > 72) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle size={12} />
          {days} dager
        </span>
      );
    }
    if (hoursDiff > 48) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <Clock size={12} />
          {days} dager
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <Clock size={12} />
        {days} {days === 1 ? 'dag' : 'dager'}
        </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-4 border-b bg-gradient-to-r from-yellow-50 to-orange-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Calendar className="text-yellow-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Til bestilling
              </h2>
              <p className="text-sm text-gray-600">Saker som venter på delebestilling</p>
            </div>
          </div>
          <span className="px-3 py-1.5 text-sm font-medium rounded-full bg-yellow-100 text-yellow-800">
            {tilBestillingCases.length} {tilBestillingCases.length === 1 ? 'sak' : 'saker'}
          </span>
        </div>
      </div>

      {moveError && (
        <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
          <AlertCircle className="text-red-600" size={16} />
          <span className="text-red-700 text-sm">{moveError}</span>
        </div>
      )}

      <div className="p-4">
        {tilBestillingCases.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <Calendar className="text-gray-400" size={32} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Ingen saker til bestilling
            </h3>
            <p className="text-gray-600">
              Saker som venter på delebestilling vil vises her
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {Object.entries(groupedCases).map(([customerName, customerCases]) => {
              // Hvis kunden kun har én ordre, vis som vanlig enkeltordre
              if (customerCases.length === 1) {
                const case_ = customerCases[0];
                return (
                  <div
                    key={case_.id}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${getUrgencyColor(case_.opprettet)}`}
                    onClick={() => openCaseForEditing(case_)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <User className="text-gray-500" size={18} />
                        <h3 className="text-base font-semibold text-gray-900">
                          {case_.kunde}
                        </h3>
                      </div>
                      {getUrgencyBadge(case_.opprettet)}
                    </div>

                    <div className="space-y-1 mb-2">
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <Package size={14} className="text-gray-400" />
                        <span className="font-medium">{case_.maskinMerke || "Ikke spesifisert"}</span>
                        {case_.serienr && (
                          <span className="text-gray-500 text-xs">• SN: {case_.serienr}</span>
                        )}
                      </div>
                      
                      <div className="text-sm text-gray-700 bg-white bg-opacity-60 rounded px-2 py-1">
                        {case_.beskrivelse}
                      </div>

                      {case_.tlf && (
                        <div className="text-xs text-gray-600">
                          📞 {case_.tlf}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-opacity-30">
                      <div className="text-xs text-gray-500">
                        {new Date(case_.opprettet).toLocaleDateString("nb-NO")}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveToCategory(case_.id, "Bestilt");
                          }}
                          disabled={isMoving === case_.id}
                          className={`
                            inline-flex items-center space-x-1 px-4 py-2 
                            bg-gradient-to-r from-green-500 to-green-600 
                            text-white text-sm font-medium rounded-lg 
                            hover:from-green-600 hover:to-green-700 
                            transition-all transform hover:scale-105
                            disabled:opacity-50 disabled:cursor-not-allowed 
                            ${isMoving === case_.id ? "animate-pulse" : ""}
                          `}
                        >
                          {isMoving === case_.id ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                              <span>Flytter...</span>
                            </>
                          ) : (
                            <>
                              <span>Flytt til bestilt</span>
                              <ChevronRight size={16} />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }
              
              // Hvis kunden har flere ordre, vis som gruppert kort
              const oldestCase = customerCases.reduce((oldest, current) => 
                new Date(current.opprettet) < new Date(oldest.opprettet) ? current : oldest
              );
              
              return (
                <div
                  key={customerName}
                  className="border-2 border-gray-200 rounded-lg overflow-hidden hover:shadow-lg hover:border-green-300 transition-all"
                >
                  <div className="px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <User className="text-gray-600" size={18} />
                      <h3 className="text-base font-semibold text-gray-900">{customerName}</h3>
                    </div>
                    <span className="px-2 py-0.5 bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-semibold rounded-full">
                      {customerCases.length} ordre
                    </span>
                  </div>
                  
                  <div className="p-1">
                    {customerCases.map((case_) => (
                      <div
                        key={case_.id}
                        className={`
                          m-1 p-2 rounded-lg cursor-pointer transition-all
                          bg-gray-50 border border-gray-200 hover:bg-white hover:border-green-400
                          relative pl-8
                        `}
                        onClick={() => openCaseForEditing(case_)}
                      >
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {case_.maskinMerke || "Ikke spesifisert"}
                              {case_.serienr && (
                                <span className="text-gray-500 text-xs ml-1">SN: {case_.serienr}</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-600">{case_.beskrivelse}</div>
                          </div>
                          {getUrgencyBadge(case_.opprettet)}
                        </div>
                        
                        <div className="flex items-center justify-between text-xs text-gray-500 mt-1.5 pt-1.5 border-t border-gray-200">
                          <span>{new Date(case_.opprettet).toLocaleDateString("nb-NO")}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveToCategory(case_.id, "Bestilt");
                            }}
                            disabled={isMoving === case_.id}
                            className={`
                              px-2 py-1 bg-gradient-to-r from-green-500 to-green-600 
                              text-white text-xs font-medium rounded
                              hover:from-green-600 hover:to-green-700 transition-all
                              disabled:opacity-50 disabled:cursor-not-allowed
                              ${isMoving === case_.id ? "animate-pulse" : ""}
                            `}
                          >
                            {isMoving === case_.id ? "..." : "→ Bestilt"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {customerCases[0].tlf && (
                    <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-600">
                      📞 {customerCases[0].tlf}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TilBestilling;